import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Clock, Check } from "lucide-react";
import { Toaster } from "sonner";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import CallNowButton from "../components/CallNowButton";
import { getPostBySlug, POSTS } from "../data/blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const easeExpo = [0.16, 1, 0.3, 1];

// Momentum Method workshop callout — shows only while the current round's
// workshop is still upcoming; switches to waitlist mode when the 8 seats fill.
function WorkshopCallout() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/workshop/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const workshopDate = new Date(stats.workshop_date_iso);
  if (isNaN(workshopDate.getTime()) || workshopDate < new Date()) return null;
  const full = stats.remaining <= 0;
  return (
    <div className="border-2 border-black mb-16" data-testid="blog-workshop-callout">
      <div className="cmyk-bar cmyk-bar-thin">
        <div className="c" /><div className="m" /><div className="y" /><div className="k" />
      </div>
      <div className="p-8 md:p-10">
        <div className="label-caps text-black/50 mb-3">
          Free live workshop · {stats.date_short} · {stats.time_label}
        </div>
        <h3 className="font-display text-3xl md:text-4xl leading-tight mb-4">
          Find 10 hours in your week with AI.
        </h3>
        <p className="font-body text-base text-black/70 leading-relaxed mb-6 max-w-xl">
          Sarah runs The Momentum Method live — 45 minutes, recorded if you can't make it.
          We'll audit where your week actually goes, and you'll leave knowing exactly which
          task to hand to AI first.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <a href="/momentum-method" className="btn-primary" data-testid="blog-workshop-cta">
            {full ? "Join the waitlist" : "Save your free workshop seat"} <ArrowRight size={16} />
          </a>
          <span className="font-mono text-xs text-black/50" data-testid="blog-workshop-spots">
            {full ? "Workshop full — waitlist open" : `${stats.remaining} of ${stats.spots} spots left`}
          </span>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
}

function updateSeo(post) {
  document.title = `${post.title} | GM Print Blog`;
  const setMeta = (name, content, prop = false) => {
    const attr = prop ? "property" : "name";
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  setMeta("description", post.excerpt);
  setMeta("og:title", `${post.title} | GM Print Blog`, true);
  setMeta("og:description", post.excerpt, true);
  setMeta("og:type", "article", true);
  if (post.image) setMeta("og:image", post.image, true);

  document.getElementById("blog-article-jsonld")?.remove();
  const script = document.createElement("script");
  script.id = "blog-article-jsonld";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "author": { "@type": "Person", "name": post.author, "jobTitle": post.authorRole || "" },
    "publisher": {
      "@type": "Organization",
      "name": "GM Print",
      "logo": { "@type": "ImageObject", "url": "https://gmprint.com.au/logos/gm-logo-light.png" }
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "image": post.image || undefined,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://gmprint.com.au/blog/${post.slug}` }
  });
  document.head.appendChild(script);
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (post) {
      updateSeo(post);
      window.scrollTo(0, 0);
    }
    return () => { document.title = "Printing, Signage & Design Brisbane | GM Print"; };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const otherPosts = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const renderBody = (item, i) => {
    if (typeof item === "string") {
      return <p key={i} className="font-body text-lg leading-relaxed text-black/85 mb-6">{item}</p>;
    }
    if (item.list) {
      return (
        <ul key={i} className="list-disc pl-6 mb-6 space-y-2 marker:text-[#EC008C]">
          {item.list.map((li, j) => (
            <li key={j} className="font-body text-lg leading-relaxed text-black/85">{li}</li>
          ))}
        </ul>
      );
    }
    if (item.heading) {
      return (
        <div key={i} className="mt-10 mb-6">
          <h3 className="font-display text-2xl md:text-3xl leading-tight mb-4">{item.heading}</h3>
          {item.body && <p className="font-body text-base md:text-lg leading-relaxed text-black/75">{item.body}</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <Navigation />
      <CallNowButton />

      <main className="pt-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 mb-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-black/50 hover:text-black transition-colors" data-testid="blog-back-link">
            <ArrowLeft size={14} /> Back to blog
          </Link>
        </div>

        <article className="max-w-[900px] mx-auto px-6 md:px-10 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center gap-3 mb-6"
          >
            <div className="w-14 h-px bg-black" />
            <span className="label-caps">{post.category || "Insight"}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: easeExpo }}
            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-8"
            data-testid="post-title"
          >
            {post.title}
          </motion.h1>

          <div className="flex flex-wrap items-center gap-6 mb-12 pb-8 border-b border-black/15">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-[#EC008C] flex items-center justify-center font-display text-lg text-black">
                {post.author?.split(" ").map(w => w[0]).join("").slice(0, 2)}
              </div>
              <div>
                <div className="font-body text-sm font-semibold" data-testid="post-author">{post.author}</div>
                {post.authorRole && <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">{post.authorRole}</div>}
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs font-mono text-black/50">
              <span className="flex items-center gap-1.5"><Calendar size={12} /> {formatDate(post.date)}</span>
              <span className="flex items-center gap-1.5"><Clock size={12} /> {post.readTime}</span>
            </div>
          </div>

          {post.image && (
            <div className="mb-12 border-2 border-black overflow-hidden">
              <img src={post.image} alt={post.title} className="w-full h-auto" />
            </div>
          )}

          <div className="prose-body">
            {Array.isArray(post.body) ? post.body.map(renderBody) : <p>{post.body}</p>}
          </div>

          <div className="mt-16">
            <WorkshopCallout />
          </div>

          <div className="mt-16 pt-10 border-t-2 border-black">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="font-display text-3xl md:text-4xl mb-1">Need this in print?</div>
                <div className="flex items-center gap-2 text-sm text-black/60"><Check size={16} /><span className="font-mono text-xs">24hr response guaranteed</span></div>
              </div>
              <a href="/#contact" className="btn-primary" data-testid="post-cta">
                Get Your Free Quote <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </article>

        {otherPosts.length > 0 && (
          <section className="bg-white border-t-2 border-black">
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
              <h2 className="font-display text-4xl md:text-5xl mb-10">More reading.</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {otherPosts.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-card group">
                    {p.image && (
                      <div className="blog-card-image">
                        <img src={p.image} alt={p.title} />
                      </div>
                    )}
                    <div className="blog-card-body">
                      <div className="flex items-center gap-3 text-xs font-mono text-black/50 mb-3">
                        <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(p.date)}</span>
                      </div>
                      <h3 className="font-display text-2xl mb-3 leading-tight">{p.title}</h3>
                      <p className="font-body text-sm text-black/70 leading-relaxed mb-4 line-clamp-2">{p.excerpt}</p>
                      <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest">Read <ArrowRight size={14} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
